import React, { useState, useEffect, useContext } from "react";
import { NavLink } from "react-router-dom";
import { menuConfig, staticMenuConfig } from "../../../configs/menuConfig";
import * as Icons from "react-icons/fa";
import ProfileImage from "../../../components/ProfileImage";
import Spinner from "../../../components/Spinner";
import "../styles/LeftMenu.css";
import { AuthContext } from "../../../context/AuthContext";

const LeftMenu = ({ menuItems, onToggle, loading, organizationLogo, organizationName, isCollapsed, isStatic = false }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [expandedMenus, setExpandedMenus] = useState({});
    const [isMobile, setIsMobile] = useState(false);
    
    // Get user data from AuthContext
    const { user } = useContext(AuthContext);
    
    // Determine user role text
    const getUserType = () => {
        if (!user) return "User";
        switch(user.userType) {
            case 0: return "Administrator";
            case 1: return "Normal User";
            case 2: return "Manager";
            default: return "User";
        }
    };

    // Get first 3 positions
    const getPositions = () => {
        if (!user?.positions) return [];
        return user.positions.slice(0, 3).map(pos => pos.positionTitle);
    };

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) {
                setMobileOpen(false);
            }
        };
        
        window.addEventListener('resize', handleResize);
        handleResize();
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Update local collapsed state when prop changes
    useEffect(() => {
        setCollapsed(isCollapsed);
    }, [isCollapsed]);

    const toggleDesktopMenu = () => {
        if (!isMobile) {
            setCollapsed(!collapsed);
            onToggle();
        }
    };

    const toggleMobileMenu = () => {
        if (isMobile) {
            setMobileOpen(!mobileOpen);
        }
    };

    const toggleSubMenu = (menuId) => {
        setExpandedMenus(prev => ({
            ...prev,
            [menuId]: !prev[menuId]
        }));
    };

    const handleMenuItemClick = () => {
        if (isMobile) {
            setMobileOpen(false); // Close the mobile menu
        }
    };

    const renderMenuItem = (item, level = 0) => {
        // Convert icon code to proper format for react-icons/fa
        const getIconComponent = (iconCode) => {
            if (!iconCode) return Icons.FaList;
            
            // If already in correct format (starts with 'Fa'), use as is
            if (iconCode.startsWith('Fa')) {
                return Icons[iconCode] || Icons.FaList;
            }
            
            // Try different variations of the icon name
            const variations = [
                `Fa${iconCode}`, // e.g., "User" -> "FaUser"
                iconCode, // Try as is
                `Fa${iconCode.charAt(0).toUpperCase()}${iconCode.slice(1)}` // e.g., "user" -> "FaUser"
            ];
            
            // Find first matching icon
            for (const variant of variations) {
                if (Icons[variant]) {
                    return Icons[variant];
                }
            }
            
            // Fallback to default icon
            return Icons.FaList;
        };

        const IconComponent = getIconComponent(item.iconCode);
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedMenus[item.id];
        const config = isStatic ? staticMenuConfig : menuConfig;
        
        return (
            <li key={item.id} className={`menu-item ${hasChildren ? 'has-children' : ''}`}>
                <div className="menu-link-container">
                    {hasChildren && (!collapsed || isMobile) && (
                        <button 
                            className="submenu-toggle"
                            onClick={() => toggleSubMenu(item.id)}
                        >
                            {isExpanded ? <Icons.FaChevronDown /> : <Icons.FaChevronRight />}
                        </button>
                    )}
                    {hasChildren ? (
                        <div 
                            className="menu-link-category"
                            onClick={handleMenuItemClick}
                        >
                            <div className="icon-container">
                                <IconComponent />
                            </div>
                            {(!collapsed || isMobile) && <span className="menu-text">{item.name}</span>}
                        </div>
                    ) : (
                        <NavLink 
                            to={config[item.code] || "#"}
                            className={({isActive}) => isActive ? 'active' : ''}
                            onClick={handleMenuItemClick}
                        >
                            <div className="icon-container">
                                <IconComponent />
                            </div>
                            {(!collapsed || isMobile) && <span className="menu-text">{item.name}</span>}
                        </NavLink>
                    )}
                </div>
                
                {hasChildren && (
                    <ul className={`submenu ${isExpanded ? 'expanded' : ''}`}>
                        {item.children.map(child => renderMenuItem(child, level + 1))}
                    </ul>
                )}
            </li>
        );
    };

    return (
        <>
            {/* Mobile hamburger icon */}
            {isMobile && (
                <button className="mobile-toggle" onClick={toggleMobileMenu}>
                    <Icons.FaBars />
                </button>
            )}
            
            <div className={`left-menu-container ${collapsed && !isMobile ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
                {/* Desktop toggle button */}
                {!isMobile && (
                    <button className="toggle-button" onClick={toggleDesktopMenu}>
                        {collapsed ? <Icons.FaAngleRight /> : <Icons.FaAngleLeft />}
                    </button>
                )}
                
                {/* Organization Logo and Name */}
                <div className="org-header">
                    <div className="org-header-content">
                        <div className="logo-container">
                            <a 
                                href="https://fec.ai" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ display: 'block' }}
                            >
                                {organizationLogo ? (
                                    <img
                                        src={organizationLogo}
                                        alt={organizationName}
                                        className="org-logo"
                                        style={{ cursor: 'pointer' }}
                                    />
                                ) : (
                                    <img
                                        src="/logo.png"
                                        alt="App Logo"
                                        className="org-logo"
                                        style={{ cursor: 'pointer' }}
                                    />
                                )}
                            </a>
                        </div>
                        {(!collapsed || isMobile) && (
                            <div className="org-name">{organizationName}</div>
                        )}
                    </div>
                </div>
                
                {/* Top section - User profile */}
                <div className="user-profile">
                    <div className="avatar-container">
                        <ProfileImage 
                            imageUrl={user?.imageUrl || ""}
                            width={60}
                            height={60}
                        />
                    </div>
                    {(!collapsed || isMobile) && (
                        <div className="user-info">
                            <h3 className="user-name">{user?.username || "User"}</h3>
                            <p className="user-type">{getUserType()}</p>
                            <div className="user-positions">
                                {getPositions().map((position, index) => (
                                    <p key={index} className="position-title">{position}</p>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Navigation section */}
                <nav className="menu-navigation">
                    {loading ? (
                        <div className="menu-loading">
                            <Spinner />
                        </div>
                    ) : (
                        <ul className="menu-list">
                            {menuItems.map(item => renderMenuItem(item, 0))}
                        </ul>
                    )}
                </nav>
                
                {/* Footer section */}
                <div className="menu-footer">
                    <NavLink to="/settings" className="footer-link">
                        <Icons.FaCog />
                        {(!collapsed || isMobile) && <span>Settings</span>}
                    </NavLink>
                    <NavLink to="/logout" className="footer-link logout">
                        <Icons.FaSignOutAlt />
                        {(!collapsed || isMobile) && <span>Logout</span>}
                    </NavLink>
                </div>
            </div>
        </>
    );
};

export default LeftMenu;
